import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  finalizarProva,
  iniciarProva,
  responder,
  type Prova,
  type QuestaoDaProva,
  type Resultado,
} from "../lib/faculdade";
import { useHub } from "../lib/dados";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import TextoRico from "../componentes/TextoRico";
import { EscudoFBC, SetaDireita, Topo } from "../componentes/ui";
import { AvisoNavy, Nota, Selo } from "../componentes/FaculdadeUi";

// A prova de uma disciplina — `/faculdade/prova/:disciplinaId`.
//
// ---------------------------------------------------------------------------
// ROTA PRÓPRIA, e não um modal dentro do boletim
// ---------------------------------------------------------------------------
// O aluno passa vários minutos aqui, volta pelo botão do navegador, e recarrega
// a página. Um modal perderia tudo isso — e é a mesma razão de as abas da
// ferramenta serem sub-rota nesta casa.
//
// ---------------------------------------------------------------------------
// Cada resposta vai para o banco na hora
// ---------------------------------------------------------------------------
// Não há "salvar rascunho": marcar a alternativa GRAVA. Guardar as dez respostas
// em memória e mandar tudo no fim é a versão em que fechar a aba no minuto nove
// perde a prova inteira — e o aluno teria queimado uma das cinco tentativas sem
// receber nota.
//
// `pos_responder` não diz se acertou, e isso é do desenho: se dissesse, a prova
// viraria tentativa e erro dentro da mesma tentativa. O gabarito só existe
// depois de `pos_finalizar_tentativa`, com a nota já gravada.
//
// ---------------------------------------------------------------------------
// Uma questão por vez
// ---------------------------------------------------------------------------
// As dez de uma vez caberiam na tela e seriam piores: o enunciado tem 249
// caracteres em média mas chega a 2.936, e algumas trazem imagem. Uma por vez
// mantém o enunciado inteiro à vista sem rolagem, que é o que a prova pede.
//
// A trilha de dez bolinhas no topo mostra onde ele está E o que já respondeu —
// sem ela, "faltam quantas?" só se responde rolando.

export default function FaculdadeProva() {
  const { disciplinaId = "" } = useParams();
  const navegar = useNavigate();
  const estadoHub = useHub();

  const [fase, setFase] = useState<"abrindo" | "prova" | "resultado" | "erro">("abrindo");
  const [erro, setErro] = useState<string>("");
  const [prova, setProva] = useState<Prova | null>(null);
  const [atual, setAtual] = useState(0);
  const [marcadas, setMarcadas] = useState<Record<string, string>>({});
  const [gravando, setGravando] = useState(false);
  const [falhaAoGravar, setFalhaAoGravar] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [finalizando, setFinalizando] = useState(false);
  const topo = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const p = await iniciarProva(disciplinaId);
        if (!vivo) return;
        setProva(p);
        // Retomando: as marcações que já existem voltam do banco.
        const jaMarcadas: Record<string, string> = {};
        for (const q of p.questoes) if (q.marcada) jaMarcadas[q.id] = q.marcada;
        setMarcadas(jaMarcadas);
        // Abre na primeira SEM resposta — retomar no começo faria o aluno rolar
        // por tudo que já fez.
        const primeiraVazia = p.questoes.findIndex((q) => !q.marcada);
        setAtual(primeiraVazia >= 0 ? primeiraVazia : 0);
        setFase("prova");
      } catch (e) {
        if (!vivo) return;
        // A mensagem vem do banco e é escrita para o aluno ("você já usou as suas
        // tentativas…", "o seu acesso a este curso não está mais vigente…").
        // Trocá-la por um texto genérico apagaria justamente o que ele precisa
        // saber para agir.
        setErro((e as { message?: string })?.message ?? "Não conseguimos abrir a prova.");
        setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, [disciplinaId]);

  if (estadoHub.fase === "carregando" || fase === "abrindo") return <Carregando />;

  const nome = estadoHub.fase === "pronto" ? estadoHub.hub.nome : null;
  const avatar = estadoHub.fase === "pronto" ? estadoHub.hub.avatarUrl : null;

  function Moldura({ children }: { children: React.ReactNode }) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#0c1f40_0%,#102850_40%,#0e2348_100%)]">
        <Topo atual="faculdade" nome={nome} avatarUrl={avatar} />
        <div className="mx-auto max-w-3xl px-4 pb-20 sm:px-6" ref={topo}>
          {children}
          <Rodape sobreNavy />
        </div>
      </div>
    );
  }

  if (fase === "erro") {
    return (
      <Moldura>
        <div className="pt-10">
          <VoltarAoBoletim />
          <div className="mt-6">
            <AvisoNavy
              titulo="A prova não abriu"
              tom="erro"
              acao={
                <Link to="/faculdade/notas" className="btn-contorno-navy">
                  Ver as minhas notas
                </Link>
              }
            >
              {erro}
            </AvisoNavy>
          </div>
        </div>
      </Moldura>
    );
  }

  if (fase === "resultado" && resultado && prova) {
    return (
      <Moldura>
        <Resultado_ resultado={resultado} prova={prova} aoSair={() => navegar("/faculdade/notas")} />
      </Moldura>
    );
  }

  if (!prova) return <Carregando />;

  const questao = prova.questoes[atual];
  const respondidas = prova.questoes.filter((q) => marcadas[q.id]).length;
  const todas = respondidas === prova.questoes.length;

  async function marcar(q: QuestaoDaProva, letra: string) {
    // Otimista na tela, e só na tela: o estado muda na hora para o clique não
    // parecer travado, e o erro (se houver) aparece explicitamente. Reverter a
    // marca no erro seria pior — o aluno veria a escolha dele sumir sozinha.
    setMarcadas((m) => ({ ...m, [q.id]: letra }));
    setGravando(true);
    setFalhaAoGravar(null);
    try {
      await responder(prova!.tentativa_id, q.id, letra);
    } catch (e) {
      setFalhaAoGravar(
        (e as { message?: string })?.message ??
          "Não conseguimos gravar esta resposta. Marque de novo antes de finalizar.",
      );
    } finally {
      setGravando(false);
    }
  }

  async function finalizar() {
    setFinalizando(true);
    try {
      const r = await finalizarProva(prova!.tentativa_id);
      setResultado(r);
      setFase("resultado");
      topo.current?.scrollIntoView({ behavior: "auto", block: "start" });
    } catch (e) {
      setFalhaAoGravar(
        (e as { message?: string })?.message ?? "Não conseguimos finalizar. Tente de novo.",
      );
    } finally {
      setFinalizando(false);
    }
  }

  return (
    <Moldura>
      <div className="pt-10">
        <VoltarAoBoletim />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-navy-mute">
              Prova · tentativa {prova.numero}
            </p>
            <h1 className="mt-1 font-display text-2xl font-bold text-white sm:text-3xl">
              {prova.disciplina}
            </h1>
          </div>
          <EscudoFBC className="hidden size-12 sm:block" />
        </div>

        {/* A trilha: onde ele está e o que já respondeu. As bolinhas são botões —
            rever uma questão antes de finalizar é parte de fazer prova. */}
        <div className="mt-6 flex flex-wrap gap-2" role="group" aria-label="Questões da prova">
          {prova.questoes.map((q, i) => {
            const feita = Boolean(marcadas[q.id]);
            const aqui = i === atual;
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => setAtual(i)}
                aria-current={aqui ? "step" : undefined}
                aria-label={`Questão ${i + 1}${feita ? ", respondida" : ", em branco"}`}
                className={`size-9 rounded-full border text-sm font-semibold tabular-nums transition ${
                  aqui
                    ? "border-laranja bg-laranja text-azul"
                    : feita
                      ? "border-sucesso-claro/40 bg-sucesso-claro/15 text-sucesso-claro"
                      : "border-navy-ink/20 text-navy-mute hover:border-navy-ink/40"
                }`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-sm text-navy-mute">
          {respondidas} de {prova.questoes.length} respondidas
          {prova.tentativas_restantes > 0
            ? ` · ${prova.tentativas_restantes} tentativa(s) restante(s) depois desta`
            : " · esta é a sua última tentativa"}
        </p>

        {/* ── A questão ─────────────────────────────────────────────────── */}
        <div className="tv-card mt-6 p-6 sm:p-7">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-navy-mute">
            Questão {atual + 1}
          </p>

          {/* `data-enunciado` é escopo de prova, como `data-alternativas` logo
              abaixo: sem ele, medir o contraste do enunciado exigiria um seletor
              por posição, e a cicatriz dos sete rótulos iguais da calculadora diz
              onde isso termina. */}
          <div data-enunciado className="mt-3 text-navy-ink">
            <BlocosDoEnunciado blocos={questao.enunciado} />
          </div>

          {/* `data-alternativas` é escopo de prova. Sem ele, contar
              `[role="radio"]` na página inteira dá SETE: as quatro daqui mais as
              três do seletor de tema no Topo (claro / escuro / automático).
              É a cicatriz dos sete rótulos iguais da calculadora — seletor sem
              escopo pega o elemento errado, e afrouxar a asserção transferiria o
              problema para a próxima pessoa. */}
          <div
            data-alternativas
            className="mt-6 flex flex-col gap-2"
            role="radiogroup"
            aria-label="Alternativas"
          >
            {questao.alternativas.map((a) => {
              const escolhida = marcadas[questao.id] === a.letra;
              return (
                <button
                  key={a.letra}
                  type="button"
                  role="radio"
                  aria-checked={escolhida}
                  onClick={() => marcar(questao, a.letra)}
                  className={`flex items-start gap-3 rounded-cf border p-4 text-left transition ${
                    escolhida
                      ? "border-laranja bg-laranja/10"
                      : "border-navy-ink/15 hover:border-navy-ink/35 hover:bg-navy-ink/5"
                  }`}
                >
                  <span
                    className={`grid size-7 shrink-0 place-items-center rounded-full text-sm font-bold ${
                      escolhida ? "bg-laranja text-azul" : "bg-navy-ink/10 text-navy-mute"
                    }`}
                  >
                    {a.letra}
                  </span>
                  <span className="text-navy-ink">{a.conteudo}</span>
                </button>
              );
            })}
          </div>
        </div>

        {falhaAoGravar && (
          <div className="mt-4">
            {/* Erro de UMA resposta não derruba a prova. É a cicatriz do
                Calendário: `setErro(true)` numa escrita trocava a tela inteira
                pela mensagem de falha, apagando o que a pessoa estava lendo. */}
            <AvisoNavy titulo="Uma resposta não foi gravada" tom="erro">
              {falhaAoGravar} As outras continuam salvas.
            </AvisoNavy>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setAtual((n) => Math.max(0, n - 1))}
            disabled={atual === 0}
            className="btn-contorno-navy disabled:opacity-40"
          >
            Anterior
          </button>

          {atual < prova.questoes.length - 1 ? (
            <button
              type="button"
              onClick={() => setAtual((n) => n + 1)}
              className="pill-cta"
              disabled={gravando}
            >
              Próxima <SetaDireita />
            </button>
          ) : (
            <button
              type="button"
              onClick={finalizar}
              disabled={finalizando || gravando}
              className="pill-cta disabled:opacity-60"
            >
              {finalizando ? "Corrigindo…" : "Finalizar e ver a nota"} <SetaDireita />
            </button>
          )}
        </div>

        {/* O aviso de questão em branco fica junto do botão de finalizar, e não
            num modal: ele informa, não pergunta. Deixar em branco é uma escolha
            legítima — vale zero, e o aluno pode preferir isso a chutar. */}
        {!todas && atual === prova.questoes.length - 1 && (
          <p className="mt-3 text-sm text-laranja-claro">
            Faltam {prova.questoes.length - respondidas} questão(ões) em branco. Questão sem
            resposta conta como erro.
          </p>
        )}
      </div>
    </Moldura>
  );
}

function VoltarAoBoletim() {
  return (
    <Link
      to="/faculdade/notas"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-mute transition hover:text-navy-ink"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-4"
        aria-hidden="true"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
      Avaliações
    </Link>
  );
}

/**
 * O enunciado, que é DADO tipado — nunca HTML.
 *
 * Os blocos vêm no vocabulário de `conteudo_blocos` (000073), e o texto de cada
 * um carrega a marcação da casa (`**negrito**`, `==marcado==`), que `TextoRico`
 * transforma em elementos. Não existe `dangerouslySetInnerHTML` no hub, e a
 * ausência é a peça de segurança: o enunciado vem de um editor externo.
 */
function BlocosDoEnunciado({ blocos }: { blocos: unknown }) {
  if (!Array.isArray(blocos)) return null;
  return (
    <>
      {blocos.map((b, i) => {
        const bloco = b as {
          tipo?: string;
          texto?: string;
          itens?: string[];
          ordenada?: boolean;
          url?: string;
        };
        if (bloco.tipo === "titulo") {
          return (
            <h2 key={i} className="mt-4 font-display text-lg font-semibold text-white first:mt-0">
              <TextoRico texto={bloco.texto ?? ""} />
            </h2>
          );
        }
        if (bloco.tipo === "lista") {
          const Lista = bloco.ordenada ? "ol" : "ul";
          return (
            <Lista
              key={i}
              className={`mt-3 flex flex-col gap-1 pl-5 ${
                bloco.ordenada ? "list-decimal" : "list-disc"
              }`}
            >
              {(bloco.itens ?? []).map((it, j) => (
                <li key={j}>
                  <TextoRico texto={it} />
                </li>
              ))}
            </Lista>
          );
        }
        if (bloco.tipo === "imagem" && bloco.url) {
          return (
            <img
              key={i}
              src={bloco.url}
              alt=""
              // Sem `loading="lazy"`: a imagem do enunciado está sempre acima da
              // dobra dentro do card, e lazy aqui atrasaria o que o aluno precisa
              // ler para responder. (E é o que faz prova de tela contar
              // "1 de 3 imagens" — a cicatriz do IR Pro.)
              className="mt-4 max-w-full rounded-cf border border-navy-ink/10"
            />
          );
        }
        return (
          <p key={i} className="mt-3 first:mt-0">
            <TextoRico texto={bloco.texto ?? ""} />
          </p>
        );
      })}
    </>
  );
}

function Resultado_({
  resultado,
  prova,
  aoSair,
}: {
  resultado: Resultado;
  prova: Prova;
  aoSair: () => void;
}) {
  return (
    <div className="pt-10">
      <div className="tv-card p-7 text-center">
        <p className="text-xs uppercase tracking-[0.16em] text-navy-mute">
          {prova.disciplina} · tentativa {prova.numero}
        </p>
        <div className="mt-4">
          <span className="font-display text-6xl font-bold tabular-nums text-white">
            {resultado.nota}
          </span>
          <span className="ml-1 text-xl font-semibold text-navy-mute">/ 10</span>
        </div>
        <p className="mt-2 text-navy-mute">
          {resultado.acertos} de {resultado.total} questões
        </p>
        {/* NÃO diz "aprovado" nem "reprovado". Existe nota mínima? A pergunta
            está aberta no mapeamento desde 31/07 e ninguém respondeu — e um
            "reprovado" errado na tela é pior que a ausência dele. */}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={aoSair} className="pill-cta">
            Voltar às minhas notas <SetaDireita />
          </button>
        </div>
      </div>

      {/* O gabarito. É o ÚNICO lugar do sistema em que a resposta certa sai do
          banco, e ele só existe depois de a nota estar gravada. */}
      <h2 className="mt-9 mb-4 text-lg font-bold text-white">O que você marcou</h2>
      <div className="flex flex-col gap-2">
        {resultado.gabarito.map((g, i) => (
          <div
            key={g.questao_id}
            className={`tv-card flex flex-wrap items-start gap-x-4 gap-y-2 p-4 ${
              g.acertou ? "" : "border-laranja/30"
            }`}
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-navy-ink/10 text-sm font-semibold text-navy-mute">
              {i + 1}
            </span>
            <p className="min-w-56 flex-1 text-sm text-navy-ink">{g.enunciado_texto}</p>
            <div className="flex shrink-0 items-center gap-2">
              {g.acertou ? (
                <Selo tom="ok">Acertou ({g.correta})</Selo>
              ) : (
                <>
                  <Selo tom="erro">
                    {g.marcada ? `Você: ${g.marcada}` : "Em branco"}
                  </Selo>
                  <Selo tom="ok">Certa: {g.correta}</Selo>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Reexportado para o boletim mostrar a nota com a mesma forma. */
export { Nota };
